// Data deduplication service using content hashing
// Use browser-compatible crypto for hash generation
import { supabase } from './supabase';
import { noderr } from '../../shared/utils/errorHandler';
import type { DataFingerprint } from '../../shared/types/analytics';

export class DeduplicationService {
  /**
   * Generate a simple hash for data deduplication (browser-compatible)
   * Using a simple string hash algorithm instead of crypto for browser compatibility
   */
  private generateHash(input: string): string {
    let hash = 0;
    if (input.length === 0) return hash.toString();
    
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to positive hex string
    return Math.abs(hash).toString(16);
  }

  /**
   * Generate a consistent hash for data deduplication
   */
  generateDataHash(data: any, source: string, type: string): string {
    // Create a normalized string representation for hashing
    let hashInput: string;
    
    if (source === 'spotify') {
      // For Spotify data, hash based on song/playlist name + metrics + date
      if (type === 'song') {
        hashInput = [
          data.song_name?.toLowerCase().trim(),
          data.streams,
          data.listeners,
          data.period_type,
          data.recorded_at
        ].join('|');
      } else if (type === 'playlist') {
        hashInput = [
          data.playlist_name?.toLowerCase().trim(),
          data.streams,
          data.listeners,
          data.followers,
          data.period_type,
          data.recorded_at
        ].join('|');
      } else {
        hashInput = JSON.stringify(data);
      }
    } else if (source === 'hypeddit') {
      // For Hypeddit data, hash based on campaign/link name + metrics + date
      if (type === 'campaign') {
        hashInput = [
          data.campaign_name?.toLowerCase().trim(),
          data.impressions,
          data.clicks,
          data.cost,
          data.recorded_at
        ].join('|');
      } else if (type === 'smartlink') {
        hashInput = [
          data.link_title?.toLowerCase().trim(),
          data.clicks,
          data.conversions,
          data.recorded_at
        ].join('|');
      } else {
        hashInput = JSON.stringify(data);
      }
    } else {
      // Fallback to JSON stringification
      hashInput = JSON.stringify(data);
    }

    return this.generateHash(hashInput);
  }

  /**
   * Check if data hash already exists in fingerprints
   */
  async checkForDuplicate(
    source: string, 
    upload_type: string, 
    data_hash: string
  ): Promise<{ isDuplicate: boolean; fingerprint?: DataFingerprint }> {
    try {
      const [error, result] = await noderr(
        supabase
          .from('data_fingerprints')
          .select('*')
          .eq('source', source)
          .eq('upload_type', upload_type)
          .eq('data_hash', data_hash)
          .limit(1)
      );

      if (error) {
        console.error('Duplicate check error:', error);
        return { isDuplicate: false };
      }

      const data = (result as any).data;
      if (!data || data.length === 0) {
        // No duplicate found
        return { isDuplicate: false };
      }

      const fingerprint = data[0];
      return { 
        isDuplicate: true, 
        fingerprint: fingerprint as DataFingerprint 
      };
    } catch (error) {
      console.error('Duplicate check error:', error);
      return { isDuplicate: false };
    }
  }

  /**
   * Record or update a data fingerprint
   */
  async recordFingerprint(
    source: string,
    upload_type: string,
    data_hash: string,
    content_preview?: string
  ): Promise<void> {
    try {
      // Check if fingerprint already exists
      const { isDuplicate, fingerprint } = await this.checkForDuplicate(source, upload_type, data_hash);

      if (isDuplicate && fingerprint) {
        // Update existing fingerprint
        await noderr(
          supabase
            .from('data_fingerprints')
            .update({
              last_seen: new Date().toISOString(),
              occurrence_count: fingerprint.occurrence_count + 1
            })
            .eq('id', fingerprint.id)
        );
      } else {
        // Create new fingerprint
        await noderr(
          supabase
            .from('data_fingerprints')
            .insert({
              source,
              upload_type,
              data_hash,
              content_preview: content_preview?.substring(0, 200), // Limit preview length
              first_seen: new Date().toISOString(),
              last_seen: new Date().toISOString(),
              occurrence_count: 1
            })
        );
      }
    } catch (error) {
      console.error('Fingerprint recording error:', error);
      // Don't throw - deduplication failure shouldn't block upload
    }
  }

  /**
   * Batch process records for deduplication
   */
  async processBatch<T extends { data_hash?: string }>(
    records: T[],
    source: string,
    upload_type: string,
    hashKey?: string
  ): Promise<{
    newRecords: T[];
    duplicateRecords: T[];
    duplicateCount: number;
    newCount: number;
  }> {
    const newRecords: T[] = [];
    const duplicateRecords: T[] = [];

    for (const record of records) {
      // Generate hash if not provided
      if (!record.data_hash) {
        const hash = this.generateDataHash(record, source, hashKey || upload_type);
        (record as any).data_hash = hash;
      }

      // Check for duplicate (ensure hash exists)
      if (!record.data_hash) {
        console.warn('Record missing data_hash, skipping deduplication check');
        newRecords.push(record);
        continue;
      }

      const { isDuplicate } = await this.checkForDuplicate(source, upload_type, record.data_hash);

      if (isDuplicate) {
        duplicateRecords.push(record);
      } else {
        newRecords.push(record);
        
        // Record the fingerprint for future deduplication
        await this.recordFingerprint(
          source, 
          upload_type, 
          record.data_hash,
          JSON.stringify(record).substring(0, 200)
        );
      }
    }

    return {
      newRecords,
      duplicateRecords,
      duplicateCount: duplicateRecords.length,
      newCount: newRecords.length
    };
  }

  /**
   * Get deduplication statistics for an upload source
   */
  async getDeduplicationStats(source: string, upload_type?: string): Promise<{
    total_fingerprints: number;
    recent_duplicates: number;
    most_duplicated: Array<{
      data_hash: string;
      occurrence_count: number;
      content_preview?: string;
      last_seen: string;
    }>;
  }> {
    try {
      let query = supabase
        .from('data_fingerprints')
        .select('*')
        .eq('source', source);

      if (upload_type) {
        query = query.eq('upload_type', upload_type);
      }

      const [error, result] = await noderr(query);
      
      if (error) {
        throw error;
      }

      const fingerprints = (result as any)?.data || [];
      const recent = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Last 7 days

      const recentDuplicates = fingerprints.filter((fp: DataFingerprint) => 
        fp.occurrence_count > 1 && new Date(fp.last_seen) > recent
      ).length;

      const mostDuplicated = fingerprints
        .filter((fp: DataFingerprint) => fp.occurrence_count > 1)
        .sort((a: DataFingerprint, b: DataFingerprint) => b.occurrence_count - a.occurrence_count)
        .slice(0, 10)
        .map((fp: DataFingerprint) => ({
          data_hash: fp.data_hash,
          occurrence_count: fp.occurrence_count,
          content_preview: fp.content_preview,
          last_seen: fp.last_seen
        }));

      return {
        total_fingerprints: fingerprints.length,
        recent_duplicates: recentDuplicates,
        most_duplicated: mostDuplicated
      };
    } catch (error) {
      console.error('Deduplication stats error:', error);
      return {
        total_fingerprints: 0,
        recent_duplicates: 0,
        most_duplicated: []
      };
    }
  }

  /**
   * Clean up old fingerprints (optional maintenance function)
   */
  async cleanupOldFingerprints(daysToKeep: number = 90): Promise<number> {
    try {
      const cutoffDate = new Date(Date.now() - daysToKeep * 24 * 60 * 60 * 1000);
      
      const [error, result] = await noderr(
        supabase
          .from('data_fingerprints')
          .delete()
          .lt('last_seen', cutoffDate.toISOString())
      );

      if (error) {
        throw error;
      }

      const deletedCount = (result as any)?.data?.length || 0;
      console.log(`Cleaned up ${deletedCount} old fingerprints`);
      return deletedCount;
    } catch (error) {
      console.error('Fingerprint cleanup error:', error);
      return 0;
    }
  }
}

export const deduplicationService = new DeduplicationService();
